export const routes = {
  customer: '/customer',
  default: '/',
  giftCard: '/giftcard',
  loyaltyLegacy: '/loyalty-legacy',
  ctgLoyaltyTransaction: '/ctg-loyalty-transaction',
  ctgLoyalty: '/ctg-loyalty',
  offersLD: '/offers-ld',
  offersV1: '/offers-v1',
  offersV2: '/offers-v2',
  orders: '/orders',
  releaseNotes: '/release-notes',
  history: '/history',
  signIn: '/signin',
  store: '/store',
  supportHistory: '/support-history',
  quests: '/quests',
};

export const nestedResources = {
  [routes.customer]: [
    routes.giftCard,
    routes.ctgLoyalty,
    routes.offersLD,
    routes.offersV1,
    routes.offersV2,
    routes.orders,
    routes.supportHistory,
    routes.quests,
  ].map((resource) => resource.replace('/', '')),
};

export const isRouteSelected = (route: string, pathName: string) => {
  // This function is not an exact science
  // One day we will migrate to React Router and use https://v5.reactrouter.com/web/api/matchPath
  // Until the this appears to be the best we can do...
  const routeResource = route.replace('/', '');
  const pathParts = pathName.split('/').filter(Boolean);
  const firstPathPart = `/${pathParts[0]}`;
  const lastPathPart = pathParts.slice(-1)[0];
  const isCurrentPathNestedResource = !!nestedResources[firstPathPart]?.includes(lastPathPart);
  if (isCurrentPathNestedResource) {
    // we want the offers tab to remain selected whether the agent is viewing offers v1 or v2
    if (lastPathPart.startsWith('offers')) {
      return routeResource.includes('offers');
    }
    // all other nested resources
    return lastPathPart === routeResource;
  }
  if (pathParts.length === 4) {
    // special case for double nested orders route: customers/:customerId/orders/:orderId
    return pathParts[2] === routeResource;
  }
  if (route === routes.giftCard) {
    return firstPathPart === route || lastPathPart === routeResource;
  }
  if (pathParts.length === 1 || pathParts.length === 2) {
    return firstPathPart === route;
  }
};

const customerIdPathSegment = (customerId: string) => {
  try {
    return encodeURIComponent(decodeURIComponent(customerId));
  } catch {
    return encodeURIComponent(customerId);
  }
};

export const expandPath = (resource: string, params: Record<string, string>) => {
  const { customerId = '', storeId = '', orderId = '', questId = '', transactionId = '' } = params;
  const customerSegment = customerId ? customerIdPathSegment(customerId) : '';
  if (resource === routes.customer && !!customerId) {
    return `${routes.customer}/${customerSegment}`;
  }
  if (resource === routes.ctgLoyalty && !!customerId) {
    return `${routes.customer}/${customerSegment}${routes.ctgLoyalty}`;
  }
  if (resource === routes.ctgLoyaltyTransaction && !!customerId && !!transactionId) {
    return `${routes.customer}/${customerSegment}${routes.ctgLoyaltyTransaction}/${transactionId}`;
  }
  if (resource === routes.orders && !!customerId && !orderId) {
    return `${routes.customer}/${customerSegment}${routes.orders}`;
  }
  if (resource === routes.orders && !!customerId && !!orderId) {
    return `${routes.customer}/${customerSegment}${routes.orders}/${orderId}`;
  }
  if (resource === routes.orders && !customerId && !!orderId) {
    return `${routes.orders}/${orderId}`;
  }
  if (resource === routes.offersLD && !!customerId) {
    return `${routes.customer}/${customerSegment}${routes.offersLD}`;
  }
  if (resource === routes.offersV1 && !!customerId) {
    return `${routes.customer}/${customerSegment}${routes.offersV1}`;
  }
  if (resource === routes.offersV2 && !!customerId) {
    return `${routes.customer}/${customerSegment}${routes.offersV2}`;
  }
  if (resource === routes.giftCard && !!customerId) {
    return `${routes.customer}/${customerSegment}${routes.giftCard}`;
  }
  if (resource === routes.supportHistory && !!customerId) {
    return `${routes.customer}/${customerSegment}${routes.supportHistory}`;
  }
  if (resource === routes.store && !!storeId) {
    return `${routes.store}/${storeId}`;
  }
  if (resource === routes.quests && !!customerId && !questId) {
    return `${routes.customer}/${customerSegment}${routes.quests}`;
  }
  if (resource === routes.quests && !!customerId && !!questId) {
    return `${routes.customer}/${customerSegment}${routes.quests}/${questId}`;
  }
  return resource;
};
